import ApiService from "../utils/ApiService";
import { toJS } from "mobx";
import { types } from "mobx-state-tree";
import moment from "moment";
import PubNub from 'pubnub';

import {
    APP_HEALTH_USER_FLAGS,
    MIXPANEL_REPORT_EVENTS,
    FILTER_BY_ALL_FORMS_FLAG,
    PUBNUB_ENCRYPTION_KEY,
    SORTING_ORDERS,
    TERRITORIES_TYPE_ALL_TERRITORIES,
    TERRITORIES_TYPE_FLAGS,
    TERRITORY_TYPE_DEVELOPMENT,
    USERS_WAU_DATA_FETCH_FLAGS_SITES,
    USERS_WAU_DATA_FETCH_FLAGS_TERRITORY
} from "../constants/GlobalConstant";
import { USER_BADGES_FLAGS } from "../constants/BadgeConstant";
import { IS_ISLAND, IS_ILLINOIS, IS_HP, IS_AMERI, IS_NIDA } from "../utils/getEnvironment";
import { getMonthName, getMonthRanges } from "../utils/CommonUtils";
import AuthStore from "../stores/AuthStore";
import SiteStore from "../stores/SiteStore";

const { DELAWARE, RHODE_ISLAND, MASSACHUSETTS, ILLINOIS } = TERRITORIES_TYPE_FLAGS;
const { ENROLLED_DELAWARE, ENROLLED_HP, ENROLLED_IHP, ENROLLED_STUDY } = USER_BADGES_FLAGS;

const pubnub = new PubNub({
    publishKey: "",
    subscribeKey: "",
    authKey: "",
    useRandomIVs: false
});

const Stats = types
    .model("Stats", {
        loading: false,
        wauChartLoading: false,
        wauChartByTerritoriesLoading: false,
        statsTabKey: 1,
        filterBy: types.maybeNull(types.string),
        selectedMonthForIllinoisQT: types.maybeNull(types.string),
        selectedMonthForRhodeIslandQT: types.maybeNull(types.string),
        selectedMonthForMassachusettsQT: types.maybeNull(types.string),
        selectedMonthForDelawareQT: types.maybeNull(types.string),
        selectedUserIdForCaseLoad: types.maybeNull(types.number),
        selectedUsernameForCaseLoad: types.maybeNull(types.string),
        selectedTerritoryForCaseLoad: types.maybeNull(types.string),
        selectedTerritoryForStatsUsers: types.maybeNull(types.string),
        allTerritories: types.array(types.string),
        statsData: types.array(types.frozen({
            eligibleUsersCount: types.maybe(types.number),
            enrolledUsersCount: types.maybe(types.number),
            notEligibleUsersCount: types.maybe(types.number),
            currentWeekUsersCount: types.maybe(types.number),
            pastWeekUsersCount: types.maybe(types.number),
        })),
        usersData: types.array(types.frozen({
            id: types.maybe(types.number),
            createdAt: types.maybe(types.string),
            notes: types.maybeNull(types.string),
            type: types.maybeNull(types.string),
            consentDoneAt: types.maybeNull(types.string),
            fullName: types.maybe(types.string),
            username: types.maybe(types.string),
            siteName: types.maybeNull(types.string),
            dmChannels: types.array(types.string),
            assignedPeerFullName: types.maybeNull(types.string),
            color: types.maybeNull(types.string),
            formCompletedAt: types.maybeNull(types.string),
            formReviewedAt: types.maybeNull(types.string),
            groups: types.array(types.string),
            thisMonthMessages: types.maybeNull(types.number),
            lastMonthMessages: types.maybeNull(types.number),
            indatorFlag: types.maybeNull(types.string),
            monthFlag: types.maybeNull(types.string),
            qtPoints: types.optional(types.frozen({})),
            barc10Date: types.optional(types.frozen({})),
        })),
        formsData: types.array(types.frozen({
            id: types.maybe(types.number),
            userId: types.maybe(types.number),
            formName: types.maybe(types.string),
            fullName: types.maybe(types.string),
            submittedFrom: types.maybe(types.string),
        })),
        userMessagesReportData: types.array(types.frozen({
            keyToDisplay: types.maybeNull(types.string),
            data: types.optional(types.frozen({
                overallData: types.optional(types.frozen({}))
            }))
        })),
        usersWauData: types.array(types.frozen({})),
        currentMonthRetentionUsers: types.array(types.frozen({})),
        pastMonthRetentionUsers: types.array(types.frozen({})),
        currentMonthUsersMessagedInCurrentMonth: types.maybeNull(types.number),
        pastMonthUsersMessagedInCurrentMonth: types.maybeNull(types.number),
        firstSectionThisWeekUsersData: types.array(types.frozen({})),
        firstSectionLastWeekUsersData: types.array(types.frozen({})),
        secondSectionLastWeekUsersData: types.array(types.frozen({})),
        secondSectionLastToLastWeekUsersData: types.array(types.frozen({})),
        firstSectionCommonUsersCount: types.maybeNull(types.number),
        secondSectionCommonUsersCount: types.maybeNull(types.number),
        overviewData: types.optional(types.frozen({
            annualGoal: types.optional(types.frozen({})),
            enrolled: types.optional(types.frozen({
                overview: types.optional(types.frozen({})),
                expanded: types.optional(types.frozen({
                    monthData: types.array(types.frozen({})),
                    weekData: types.array(types.frozen({}))
                }))
            })),
            engagement: types.optional(types.frozen({
                overview: types.optional(types.frozen({})),
                expanded: types.optional(types.frozen({
                    peerQTsBreakdown: types.array(types.frozen({})),
                }))
            })),
            outreach: types.optional(types.frozen({
                overview: types.optional(types.frozen({})),
                expanded: types.optional(types.frozen({
                    quarterlyData: types.array(types.frozen({})),
                }))
            })),
        })),
        usersQTsBreakdownData: types.optional(types.frozen({
            name: types.maybeNull(types.string),
            qt: types.maybeNull(types.number)
        })),
        usersWauDataBySites: types.optional(types.frozen({})),
        usersWauDataByTerritories: types.optional(types.frozen({})),
        usersQTDataForDelaware: types.optional(types.frozen({})),
        usersQTDataForRhodeIsland: types.optional(types.frozen({})),
        usersQTDataForMassachusetts: types.optional(types.frozen({})),
        usersQTDataForIllinois: types.optional(types.frozen({})),
        pagination: types.frozen({
            pageSize: 50,
            total: 0,
            current: 1,
        }),
        sortColumn: types.maybeNull(types.string),
        sortOrder: types.maybeNull(types.string)
    })
    .actions((self) => ({
        async getUsersCount() {
            try {
                if (self.statsTabKey === 1) {
                    self.setLoading(true);
                    const response = await ApiService.getRequest("users-count");
                    const { success, data } = response;
                    if (success) {
                        const cloneArr = [...data.usersData];
                        self.setStatsData(cloneArr);
                    }
                    self.setLoading(false);
                }
            } catch (error) {
                self.setLoading(false);
            }
        },
        async getUsersList() {
            try {
                self.setLoading(true);
                const { statsTabKey, selectedTerritoryForStatsUsers, pagination: { pageSize: limit, current } } = self;
                const params = {
                    limit,
                    skip: (current - 1) * limit,
                    isAssignedPeerDetailsRequired: true
                };
                if (selectedTerritoryForStatsUsers && selectedTerritoryForStatsUsers !== TERRITORIES_TYPE_ALL_TERRITORIES) {
                    params['territory'] = selectedTerritoryForStatsUsers;
                }
                if (statsTabKey === 2) {
                    params['filterBy'] = 'enrolled';
                    params['isDMChannelsRequired'] = true;
                } else if (statsTabKey === 3) {
                    params['filterBy'] = 'eligible';
                } else {
                    params['filterBy'] = 'not-eligible';
                }

                const response = await ApiService.getRequest("users-list", params);
                const { success, data, meta } = response;
                if (success) {
                    const cloneArr = [...data];
                    self.setUsersData(cloneArr);
                    self.setPaginationData(self.parseMetaData(meta));
                }
                self.setLoading(false);
            } catch (error) {
                self.setLoading(false);
            }
        },
        async getFormsData() {
            try {
                const { statsTabKey, filterBy = FILTER_BY_ALL_FORMS_FLAG, pagination: { pageSize: limit, current } } = self;
                if (statsTabKey === 5) {
                    self.setLoading(true);
                    const params = {
                        include: true,
                        includeOnly: 'forms,users',
                        isSpecificKeysRequiredForUser: true,
                        isNeededUserSubmittedForms: true,
                        $limit: limit,
                        $skip: (current - 1) * limit,
                        '$sort[createdAt]': -1,
                        filterBy,
                        isAssignedPeerDetailsRequired: true,
                        source: 'WEB'
                    };

                    const response = await ApiService.getRequest("user-forms", params);
                    const { success, data, meta } = response;
                    if (success) {
                        const filterArr = self.modifyFormsDataArr(data);
                        self.setFormsData(filterArr);
                        self.setPaginationData(self.parseMetaData(meta));
                    }
                    self.setLoading(false);
                }
            } catch (error) {
                self.setLoading(false);
            }
        },
        modifyFirstArrByComparingWithOther(mainArr = [], compareArr = [], flagToAdd = '', isFirstSection = false) {
            const copyArr = [];
            let commonUsersCount = 0;
            for (const userObj of mainArr) {
                const { username } = userObj;
                const index = compareArr.findIndex(obj => obj.username === username);
                userObj['statusFlag'] = index > -1 ? APP_HEALTH_USER_FLAGS.COMMON : flagToAdd;
                copyArr.push({ ...userObj });
                commonUsersCount += index > -1 ? 1 : 0;
            }
            if (isFirstSection) {
                self.setFirstSectionCommonUsersCount(commonUsersCount);
            } else {
                self.setSecondSectionCommonUsersCount(commonUsersCount);
            }
            return copyArr.sort((a, b) => (a.statusFlag > b.statusFlag) ? 1 : (b.statusFlag > a.statusFlag) ? -1 : 0);
        },
        setFlagForCommonAndUniqueElementsOfArray(firstArr, secondArr, isFirstSection = false) {
            const thisWeekUsersData = self.modifyFirstArrByComparingWithOther(firstArr, secondArr, APP_HEALTH_USER_FLAGS.THIS_WEEK, isFirstSection);
            const lastWeekUsersData = self.modifyFirstArrByComparingWithOther(secondArr, firstArr, APP_HEALTH_USER_FLAGS.LAST_WEEK, isFirstSection);

            return { lastWeekUsersData, thisWeekUsersData };
        },
        modifyUsersWauDataForChart(usersWauData) {
            const finalArrForChart = [];
            for (let key in usersWauData) {
                const isKeyMatchedWithCurrentWeek = moment(key).format('YYYY-MM-DD') === moment().startOf('week').subtract(-1, 'days').format('YYYY-MM-DD');
                const isKeyMatchedWithLastWeek = moment(key).format('YYYY-MM-DD') === moment().startOf('week').subtract(6, 'days').format('YYYY-MM-DD');
                const value = usersWauData[key];
                const currentValue = isKeyMatchedWithCurrentWeek ? 0 : value;
                const predictedValue = isKeyMatchedWithCurrentWeek ? value : isKeyMatchedWithLastWeek ? value : 0;
                const tempObj = { name: moment(key).format('MMM DD YYYY'), date: key };
                if (isKeyMatchedWithLastWeek) {
                    tempObj['predictedValue'] = predictedValue;
                    tempObj['currentValue'] = currentValue;
                } else if (isKeyMatchedWithCurrentWeek) {
                    tempObj['predictedValue'] = predictedValue;
                } else {
                    tempObj['currentValue'] = currentValue;
                }
                finalArrForChart.push(tempObj);
            };
            return finalArrForChart.sort((a, b) => new Date(a.date) - new Date(b.date));
        },
        async getUsersWauData() {
            try {
                self.setLoading(true);
                const REPORT_USER_WAU_DATA = 'User WAU Data';

                const params = { 'report[]': REPORT_USER_WAU_DATA };

                const response = await ApiService.getRequest("mixpanel-reports", params);

                const { success, data } = response;
                if (success && data.length) {
                    const [usersWauData] = data;
                    const { jsonData } = usersWauData || {};
                    if (jsonData && Object.keys(jsonData).length) {
                        const wauDataForChart = self.modifyUsersWauDataForChart(jsonData);
                        self.setUsersWauData(wauDataForChart);
                    }
                }
                self.setLoading(false);
            } catch (error) {
                self.setLoading(false);
            }
        },
        async fetchAppHealthData() {
            try {
                self.setLoading(true);
                const params = {};
                for (const [index, reportEventObj] of MIXPANEL_REPORT_EVENTS.entries()) {
                    const { userMessagesDataEvent } = reportEventObj;
                    params[`report[${index}]`] = userMessagesDataEvent;
                }

                const response = await ApiService.getRequest("mixpanel-reports", params);

                const { success, data } = response;
                if (success && data.length) {
                    const userMessagesReportData = [];
                    let isCurrentSiteMatched = false;
                    let currentSiteDataObj = {};
                    for (const dataObj of data) {
                        const { name, jsonData } = dataObj;
                        const lowerCaseName = name.toLowerCase();
                        const siteNameToDisplay = name.split('-')[1].trim();
                        const keyToDisplay = siteNameToDisplay.replace(/([a-z])([A-Z])/g, '$1 $2');

                        if (!isCurrentSiteMatched &&
                            ((IS_ISLAND && lowerCaseName.includes('island'))
                                || (IS_ILLINOIS && lowerCaseName.includes('illinois'))
                                || (IS_HP && lowerCaseName.includes('newengland'))
                                || (IS_AMERI && lowerCaseName.includes('amerihealth')))) {
                            isCurrentSiteMatched = true;
                            currentSiteDataObj = jsonData ? { ...jsonData } : {};
                            userMessagesReportData.push({
                                keyToDisplay,
                                data: jsonData ? { ...jsonData } : {}
                            });
                        }

                        // We will enable it for all sites, as of now we only need current site data
                        // if (jsonData && Object.keys(jsonData).length) {
                        //     userMessagesReportData.push({
                        //         keyToDisplay,
                        //         data: { ...jsonData }
                        //     });
                        // }
                    }
                    if (isCurrentSiteMatched) {
                        const { thisWeekUsersData = [], lastWeekUsersData = [], lastToLastWeekUsersData = [] } = currentSiteDataObj;
                        const {
                            thisWeekUsersData: firstSectionThisWeekUsers,
                            lastWeekUsersData: firstSectionLastWeekUsers
                        } = self.setFlagForCommonAndUniqueElementsOfArray(thisWeekUsersData, lastWeekUsersData, true);
                        const {
                            thisWeekUsersData: secondSectionLastWeekUsers,
                            lastWeekUsersData: secondSectionLastToLastWeekUsers
                        } = self.setFlagForCommonAndUniqueElementsOfArray(lastWeekUsersData, lastToLastWeekUsersData, false);
                        self.setFirstSectionThisWeekUsersData(firstSectionThisWeekUsers);
                        self.setFirstSectionLastWeekUsersData(firstSectionLastWeekUsers);
                        self.setSecondSectionLastWeekUsersData(secondSectionLastWeekUsers);
                        self.setSecondSectionLastToLastWeekUsersData(secondSectionLastToLastWeekUsers);
                    }
                    self.setUserMessagesReportData(userMessagesReportData);
                }
                self.setLoading(false);
            } catch (error) {
                self.setLoading(false);
            }
        },
        async fetchOverviewData() {
            try {
                self.setLoading(true);
                const overviewDataUrl = 'https://app-25476.on-aptible.com';
                const body = { userid: "1E5A8B2421EDBC92C66EBC881D35F" };
                const response = await ApiService.postRequest("dashboard", body, true, overviewDataUrl);
                const { success, data } = response;
                if (success && data && Object.keys(data).length) {
                    // From the data, we are decrypting some information using pubnub methods & key
                    const decryptedData = pubnub.decrypt(data?.encryptedPacket, PUBNUB_ENCRYPTION_KEY);
                    if (decryptedData && decryptedData.length) {
                        const updatedDecryptedData = decryptedData.map(({ QT, ...rest }) => ({ ...rest, qt: QT }));
                        // We are storing users qt breakdown data to our mobx state and iterate it over overview page
                        self.setUsersQTsBreakdownData(updatedDecryptedData);
                    }
                    self.setOverviewData(data);
                }
                self.setLoading(false);
            } catch (error) {
                self.setLoading(false);
            }
        },
        async getRetentionData() {
            try {
                self.setLoading(true);
                const REPORT_USERS_RETENTION_DATA = 'Users Retention Data';

                const params = { 'report[]': REPORT_USERS_RETENTION_DATA };

                const response = await ApiService.getRequest("mixpanel-reports", params);

                const { success, data } = response;
                if (success && data.length) {
                    const [usersWauData] = data;
                    const { jsonData } = usersWauData || {};
                    if (jsonData && Object.keys(jsonData).length) {
                        const { currentMonthRetentionUsers, pastMonthRetentionUsers } = jsonData;
                        const pastMonthUsersMessagedInCurrentMonth = pastMonthRetentionUsers.filter(obj => obj.statusFlag === 'GREEN');
                        const currentMonthUsersMessagedInCurrentMonth = currentMonthRetentionUsers.filter(obj => obj.statusFlag === 'GREEN');
                        self.setCurrentMonthUsersMessagedInCurrentMonth(currentMonthUsersMessagedInCurrentMonth.length || 0);
                        self.setPastMonthUsersMessagedInCurrentMonth(pastMonthUsersMessagedInCurrentMonth.length || 0);
                        self.setCurrentMonthRetentionUsers(currentMonthRetentionUsers);
                        self.setPastMonthRetentionUsers(pastMonthRetentionUsers);
                    }
                }
                self.setLoading(false);
            } catch (error) {
                self.setLoading(false);
            }
        },
        async fetchUsersWauDataBySites(paramsData = {}) {
            const { sitesArr = [], territory = [], fetchType = USERS_WAU_DATA_FETCH_FLAGS_SITES } = paramsData;
            // Check if fetch type is territory or not then we fetch its wau chart type
            const isWauChartNeededForTerritory = fetchType === USERS_WAU_DATA_FETCH_FLAGS_TERRITORY;
            try {
                if (isWauChartNeededForTerritory) {
                    self.setWauChartByTerritoriesLoading(true);
                } else {
                    self.setWauChartLoading(true);
                }
                // We will use this report to retrieve records from database and then return to the api response after doing some filtering
                let REPORT_USER_MESSAGES_DATA = 'User Messages Data - Island';
                if (IS_ILLINOIS) {
                    REPORT_USER_MESSAGES_DATA = 'User Messages Data - Illinois';
                } else if (IS_HP) {
                    REPORT_USER_MESSAGES_DATA = 'User Messages Data - NewEngland';
                } else if (IS_AMERI) {
                    REPORT_USER_MESSAGES_DATA = 'User Messages Data - AmeriHealth';
                }
                const params = { 'report': REPORT_USER_MESSAGES_DATA, };
                if (sitesArr && sitesArr.length) {
                    params['siteIds'] = sitesArr.toString();
                }
                if (territory && territory.length) {
                    for (const [index, territoryName] of territory.entries()) {
                        params[`territory[${index}]`] = territoryName;
                    }
                }
                // Fetch wau reports data according to territory or site ids
                const response = await ApiService.getRequest("wau-reports", params);
                const { success, data } = response;
                if (success && data && Object.keys(data).length) {
                    const wauDataForChart = self.modifyUsersWauDataForChart(data);
                    if (isWauChartNeededForTerritory) {
                        self.setUsersWauDataByTerritories(wauDataForChart);
                    } else {
                        self.setUsersWauDataBySites(wauDataForChart);
                    }
                }
                if (isWauChartNeededForTerritory) {
                    self.setWauChartByTerritoriesLoading(false);
                    return;
                }
                self.setWauChartLoading(false);
            } catch (error) {
                if (isWauChartNeededForTerritory) {
                    self.wauChartByTerritoriesLoading(false);
                } else {
                    self.setWauChartLoading(false);
                }
            }
        },
        async fetchCaseLoadData(userId, orderBy = null) {
            try {
                self.setLoading(true);
                const { pagination: { current, }, sortColumn, sortOrder, selectedTerritoryForCaseLoad } = self;
                const limit = 200;
                const params = {
                    skip: (current - 1) * limit,
                    limit
                };
                if (sortColumn && sortOrder && sortOrder !== 'unsorted') {
                    params['sortBy'] = sortColumn;
                    params['sortOrder'] = orderBy ? orderBy : SORTING_ORDERS[sortOrder] || null;
                }
                if (selectedTerritoryForCaseLoad && selectedTerritoryForCaseLoad !== TERRITORIES_TYPE_ALL_TERRITORIES) {
                    params['territory'] = selectedTerritoryForCaseLoad;
                }

                const userID = userId === -1 ? 'NA' : (userId === -2 ? 'WP' : (userId === -3 ? 'EN' : userId))
                //  we will set NA as user id if Not Assigned is selected in case load dropdown otherwise user id will sent in api call
                const response = await ApiService.getRequest("users-list", params, userID);
                const { success, data, meta } = response;
                if (success) {
                    const cloneArr = [...data];
                    self.setUsersData(cloneArr);
                    self.setPaginationData(self.parseMetaData(meta));
                }
                self.setLoading(false);
            } catch (error) {
                self.setLoading(false);
            }
        },
        getTotalCountsForQTUsers(qtUsersData = []) {
            let totalQTCountForUsers = 0;
            let totalUsersCountInQT = 0;
            let totalUsersCountMoreThanTwoInQT = 0;
            let totalEnrolledUsersCount = 0;
            let totalEnrolledUsersCountMoreThanTwoInQT = 0;
            if (qtUsersData.length) {
                for (const userObj of qtUsersData) {
                    const { totalScore, badgeType } = userObj;
                    // Add custom sorting flag to sort by enum, by default the value is 0
                    let badgeTypeSort = 0;
                    totalQTCountForUsers += totalScore;
                    const isTotalScoreGreaterThanTwo = totalScore >= 2;
                    const isUserIsEnrolled = badgeType === ENROLLED_DELAWARE || badgeType === ENROLLED_HP || badgeType === ENROLLED_IHP || badgeType === ENROLLED_STUDY;
                    // For enrolled, we will set flag to 1
                    if (isUserIsEnrolled) {
                        badgeTypeSort = 1;
                    } else if (!badgeType) {
                        // If badge type is null then we will set to -1
                        badgeTypeSort = -1;
                    }

                    if (isTotalScoreGreaterThanTwo) {
                        if (isUserIsEnrolled) {
                            totalEnrolledUsersCountMoreThanTwoInQT += 1;
                        }
                        totalUsersCountMoreThanTwoInQT += 1;
                    } else {
                        const finalCount = totalScore ? 1 : 0;
                        if (isUserIsEnrolled) {
                            totalEnrolledUsersCount += finalCount;
                        }
                        totalUsersCountInQT += finalCount;
                    }
                    userObj['badgeTypeSort'] = badgeTypeSort;
                }
            }
            return {
                totalQTCountForUsers,
                totalUsersCountInQT,
                totalUsersCountMoreThanTwoInQT,
                totalEnrolledUsersCount,
                totalEnrolledUsersCountMoreThanTwoInQT
            };
        },
        async makeApiCallToFetchQTData(paramsData = {}) {
            try {
                const { territory, params = {}, monthName } = paramsData;
                // Make api call to fetch data of qualified touchpoint by territory
                const response = await ApiService.getRequest("qualified-touchpoint", params);
                const { success, data } = response;
                if (success) {
                    const { firstMonth = [] } = data;

                    const {
                        totalQTCountForUsers,
                        totalUsersCountInQT,
                        totalUsersCountMoreThanTwoInQT,
                        totalEnrolledUsersCount,
                        totalEnrolledUsersCountMoreThanTwoInQT
                    } = self.getTotalCountsForQTUsers(firstMonth);

                    // Here data will be available in first month key
                    if (firstMonth && firstMonth.length) {
                        firstMonth.sort((a, b) => b.badgeTypeSort - a.badgeTypeSort);
                        // Here we are pushing the object for total count
                        firstMonth.push({
                            totalKey: `Total QT in ${monthName}`,
                            totalScore: totalQTCountForUsers || 0,
                        }, {
                            totalKey: `Total users who have 1 QT`,
                            totalScore: totalUsersCountInQT,
                        }, {
                            totalKey: `Total users who have 2+ QT`,
                            totalScore: totalUsersCountMoreThanTwoInQT,
                        }, {
                            totalKey: `Total enrolled users who have 1 QT`,
                            totalScore: totalEnrolledUsersCount,
                        }, {
                            totalKey: `Total enrolled users who have 2+ QT`,
                            totalScore: totalEnrolledUsersCountMoreThanTwoInQT,
                        });

                    }
                    // Here we are setting the key in array of object by using map function
                    const allMonthsData = [...firstMonth].map((obj, index) => ({ ...obj, key: index + 1 }))
                    // Here we will set the data according to the territory
                    if (territory === DELAWARE) {
                        self.setUsersQTDataForDelaware([...allMonthsData]);
                    } else if (territory === RHODE_ISLAND) {
                        self.setUsersQTDataForRhodeIsland([...allMonthsData]);
                    } else if (territory === MASSACHUSETTS) {
                        self.setUsersQTDataForMassachusetts([...allMonthsData]);
                    } else if (territory === ILLINOIS) {
                        self.setUsersQTDataForIllinois([...allMonthsData]);
                    }
                }
            } catch (error) {
                throw error;
            }
        },
        async fetchUsersQTData(params = {}) {
            try {
                self.setLoading(true);
                const { isMultipleTerritoriesDataRequired = false, territoryType, selectedMonth } = params;
                // Initially when component mount then we will fetch all data from all territories for current month
                // That's why we are using "isMultipleTerritoriesDataRequired" flag
                if (isMultipleTerritoriesDataRequired) {
                    // Fetch all territories names from sites store 
                    const territoriesNames = Object.keys(SiteStore.allTerritories);
                    // Get name of current month in MMM format like this -> "Jun", "Jul" etc.
                    const currentMonth = getMonthName(0, 'MMM');
                    // Find out start date and end date of month from the current month
                    const { startDateOfMonth: fromDate, endDateOfMonth: toDate } = getMonthRanges(0, 'YYYY-MM-DD', currentMonth);
                    // Create a params object of fromDate and toDate for the current month
                    const params = {
                        fromDate,
                        toDate
                    };
                    // If multiple territories are available then we will add users QT data by territory
                    if (territoriesNames && territoriesNames.length) {

                        for (let territory of territoriesNames) {
                            // We don't need data if territory is "Development" so we will skip that iteration
                            if (territory === TERRITORY_TYPE_DEVELOPMENT) {
                                continue;
                            }

                            // We will bind the territory in params object before the api call
                            params['territoryType'] = territory;
                            // We will make the api call to fetch the data based on territory and set the data according to the territory
                            await self.makeApiCallToFetchQTData({
                                territory,
                                params: { ...params },
                                monthName: currentMonth
                            });
                        }
                    }
                } else if (territoryType && selectedMonth) {
                    const { startDateOfMonth: fromDate, endDateOfMonth: toDate } = getMonthRanges(0, 'YYYY-MM-DD', selectedMonth);
                    const params = {
                        fromDate,
                        toDate,
                        territoryType
                    };
                    // We will make the api call to fetch the data based on territory and set the data according to the territory
                    await self.makeApiCallToFetchQTData({
                        territory: territoryType,
                        params: { ...params },
                        monthName: selectedMonth
                    });
                }

                self.setLoading(false);
            } catch (error) {
                self.setLoading(false);
            }
        },
        fetchAllTerritoriesBySites(sitesData) {
            if (sitesData) {
                const territoriesData = [...new Set(Object.values(sitesData).map(site => site.territory))];
                self.setAllTerritories(territoriesData.sort());
            }
        },
        modifyFormsDataArr(formsData = []) {
            const modifiedFormsArr = [];
            if (formsData.length) {
                for (const formObj of formsData) {
                    const { id,
                        submittedFrom,
                        createdAt,
                        userId,
                        user,
                        form,
                        ...rest
                    } = formObj;
                    if (user && form) {
                        const { username, notes, color, badgeType, fullName, assignedPeersDetails = {} } = user;
                        const { name } = form;
                        const { assignedPeerUserDetails, submittedByUserDetails, ...otherPeerDetails } = assignedPeersDetails;
                        const fullDetailsOfAssignedPeer = { ...otherPeerDetails };
                        if (assignedPeerUserDetails && Object.keys(assignedPeerUserDetails).length) {
                            fullDetailsOfAssignedPeer['assignedPeerFullName'] = assignedPeerUserDetails.fullName;
                        }
                        if (submittedByUserDetails && Object.keys(submittedByUserDetails).length) {
                            fullDetailsOfAssignedPeer['submittedByFullName'] = submittedByUserDetails.fullName;
                        }
                        const formDataObj = {
                            id,
                            userId,
                            submittedFrom,
                            fullName,
                            formName: name,
                            createdAt,
                            username,
                            notes,
                            color,
                            badgeType,
                            assignedPeersDetails: { ...fullDetailsOfAssignedPeer },
                            ...rest
                        };
                        modifiedFormsArr.push(formDataObj);
                    }
                }
            }
            return modifiedFormsArr;
        },
        parseMetaData(meta) {
            return {
                pageSize: meta.limit,
                current: meta.skip / meta.limit + 1,
                total: meta.total,
            };
        },
        async applyWellnessPlanOrder(arr) {
            return Promise.all(arr.map(a => {
                // Default blank status
                let status = ""
                if (a.wellnessPlan) {
                    // Check for in-progress status
                    let inProgress = a.wellnessPlan.filter(wp => {
                        if (wp.questioncount != 0 && (wp.questioncount > 0 && wp.answercount != wp.questioncount)) return wp
                    })
                    // Set status in-progress
                    status = inProgress.length ? "in-progress" : ""

                    // skip status check if it is already set
                    if (!status) {
                        // Check for completed status
                        let completed = a.wellnessPlan.filter(wp => {
                            if (wp.questioncount != 0 && (wp.questioncount > 0 && wp.answercount == wp.questioncount)) return wp
                        })
                        // Set status completed
                        status = completed.length ? "completed" : ""
                    }

                    a.wellnessPlanSortIndex = (status == "completed" ? 2 : 1)
                } else {
                    a.wellnessPlanSortIndex = 0
                }
                return a
            }))
        },
        getSortOrder(column) {
            return self.sortColumn === column ? self.sortOrder : null;
        },
        reset() {
            self.usersData = [];
            self.formsData = [];
            self.thisWeekUsersData = [];
            self.lastWeekUsersData = [];
            self.firstSectionThisWeekUsersData = [];
            self.firstSectionLastWeekUsersData = [];
            self.secondSectionLastWeekUsersData = [];
            self.secondSectionLastToLastWeekUsersData = [];
            self.currentMonthRetentionUsers = [];
            self.pastMonthRetentionUsers = [];
            self.currentMonthUsersMessagedInCurrentMonth = null;
            self.pastMonthUsersMessagedInCurrentMonth = null;
            self.userMessagesReportData = [];
            self.usersWauData = [];
            self.overviewData = {};
            self.usersWauDataBySites = {};
            self.usersWauDataByTerritories = {};
            self.usersQTDataForDelaware = [];
            self.usersQTDataForRhodeIsland = [];
            self.usersQTDataForMassachusetts = [];
            self.usersQTDataForIllinois = [];
            self.firstSectionCommonUsersCount = null;
            self.secondSectionCommonUsersCount = null;
            self.filterBy = null;
            self.wauChartLoading = false;
            self.wauChartByTerritoriesLoading = false;
            self.usersQTsBreakdownData = [];
            self.resetPagination();
            self.selectedMonthForIllinoisQT = null;
            self.selectedMonthForRhodeIslandQT = null;
            self.selectedMonthForMassachusettsQT = null;
            self.selectedMonthForDelawareQT = null;
            self.selectedUserIdForCaseLoad = null;
            self.selectedUsernameForCaseLoad = null;
            self.selectedTerritoryForCaseLoad = null;
            self.selectedTerritoryForStatsUsers = null;
            self.sortColumn = null;
            self.sortOrder = null;
            self.allTerritories = [];
        },
        resetPagination() {
            self.pagination = {
                pageSize: 50,
                total: 0,
                current: 1,
            };
        },
        setAllTerritories(data) {
            self.allTerritories = data;
        },
        setSortParameters(column, order) {
            self.sortColumn = column;
            self.sortOrder = order;
        },
        resetSortParameters() {
            self.sortColumn = null;
            self.sortOrder = null;
        },
        setWauChartLoading(value) {
            self.wauChartLoading = value;
        },
        setWauChartByTerritoriesLoading(value) {
            self.wauChartByTerritoriesLoading = value;
        },
        setUsersWauDataByTerritories(data) {
            self.usersWauDataByTerritories = data;
        },
        setUsersWauDataBySites(data) {
            self.usersWauDataBySites = data;
        },
        setFilterBy(value) {
            self.filterBy = value;
        },
        setUsersQTsBreakdownData(arr) {
            arr.sort((a, b) => b.qt - a.qt);
            self.usersQTsBreakdownData = arr;
        },
        setSelectedMonthForIllinoisQT(value) {
            self.selectedMonthForIllinoisQT = value;
        },
        setSelectedMonthForRhodeIslandQT(value) {
            self.selectedMonthForRhodeIslandQT = value;
        },
        setSelectedMonthForMassachusettsQT(value) {
            self.selectedMonthForMassachusettsQT = value;
        },
        setSelectedMonthForDelawareQT(value) {
            self.selectedMonthForDelawareQT = value;
        },
        setSelectedUserIdForCaseLoad(value) {
            self.selectedUserIdForCaseLoad = value;
        },
        setSelectedUsernameForCaseLoad(value) {
            self.selectedUsernameForCaseLoad = value;
        },
        setSelectedTerritoryForCaseLoad(value) {
            self.selectedTerritoryForCaseLoad = value;
        },
        setSelectedTerritoryForStatsUsers(value) {
            self.selectedTerritoryForStatsUsers = value;
        },
        setPaginationData(pagination) {
            self.pagination = pagination;
        },
        setUsersData(arr) {
            const modifiedArr = arr.map(obj => (
                {
                    ...obj,
                    qtPoints: { totalPoints: obj.totalPoints, lastCompletedAt: obj.lastCompletedAt },
                    barc10Date: { formReviewedAt: obj.formReviewedAt, formCompletedAt: obj.formCompletedAt, formSendAt: obj.formSendAt },
                    indator: { indatorFlag: obj.indatorFlag, monthFlag: obj.monthFlag, thisMonthMessages: obj.thisMonthMessages, lastMonthMessages: obj.lastMonthMessages }
                }
            ))
            self.usersData = modifiedArr;
        },
        setUsersWauData(arr) {
            self.usersWauData = arr;
        },
        setUsersQTDataForDelaware(arr) {
            self.usersQTDataForDelaware = arr;
        },
        setUsersQTDataForRhodeIsland(arr) {
            self.usersQTDataForRhodeIsland = arr;
        },
        setUsersQTDataForMassachusetts(arr) {
            self.usersQTDataForMassachusetts = arr;
        },
        setUsersQTDataForIllinois(arr) {
            self.usersQTDataForIllinois = arr;
        },
        setOverviewData(data) {
            self.overviewData = data;
        },
        setUserMessagesReportData(arr) {
            self.userMessagesReportData = arr;
        },
        setFirstSectionThisWeekUsersData(arr) {
            self.firstSectionThisWeekUsersData = arr;
        },
        setFirstSectionLastWeekUsersData(arr) {
            self.firstSectionLastWeekUsersData = arr;
        },
        setSecondSectionLastWeekUsersData(arr) {
            self.secondSectionLastWeekUsersData = arr;
        },
        setSecondSectionLastToLastWeekUsersData(arr) {
            self.secondSectionLastToLastWeekUsersData = arr;
        },
        setFirstSectionCommonUsersCount(value) {
            self.firstSectionCommonUsersCount = value;
        },
        setSecondSectionCommonUsersCount(value) {
            self.secondSectionCommonUsersCount = value;
        },
        setCurrentMonthRetentionUsers(arr) {
            self.currentMonthRetentionUsers = arr;
        },
        setPastMonthRetentionUsers(arr) {
            self.pastMonthRetentionUsers = arr;
        },
        setCurrentMonthUsersMessagedInCurrentMonth(value) {
            self.currentMonthUsersMessagedInCurrentMonth = value;
        },
        setPastMonthUsersMessagedInCurrentMonth(arr) {
            self.pastMonthUsersMessagedInCurrentMonth = arr;
        },
        setFormsData(arr) {
            self.formsData = arr;
        },
        setUsersWauData(arr) {
            self.usersWauData = arr;
        },
        setUsersQTDataForDelaware(arr) {
            self.usersQTDataForDelaware = arr;
        },
        setUsersQTDataForRhodeIsland(arr) {
            self.usersQTDataForRhodeIsland = arr;
        },
        setUsersQTDataForMassachusetts(arr) {
            self.usersQTDataForMassachusetts = arr;
        },
        setUsersQTDataForIllinois(arr) {
            self.usersQTDataForIllinois = arr;
        },
        setOverviewData(data) {
            self.overviewData = data;
        },
        setUserMessagesReportData(arr) {
            self.userMessagesReportData = arr;
        },
        setFirstSectionThisWeekUsersData(arr) {
            self.firstSectionThisWeekUsersData = arr;
        },
        setFirstSectionLastWeekUsersData(arr) {
            self.firstSectionLastWeekUsersData = arr;
        },
        setSecondSectionLastWeekUsersData(arr) {
            self.secondSectionLastWeekUsersData = arr;
        },
        setSecondSectionLastToLastWeekUsersData(arr) {
            self.secondSectionLastToLastWeekUsersData = arr;
        },
        setFirstSectionCommonUsersCount(value) {
            self.firstSectionCommonUsersCount = value;
        },
        setSecondSectionCommonUsersCount(value) {
            self.secondSectionCommonUsersCount = value;
        },
        setCurrentMonthRetentionUsers(arr) {
            self.currentMonthRetentionUsers = arr;
        },
        setPastMonthRetentionUsers(arr) {
            self.pastMonthRetentionUsers = arr;
        },
        setCurrentMonthUsersMessagedInCurrentMonth(value) {
            self.currentMonthUsersMessagedInCurrentMonth = value;
        },
        setPastMonthUsersMessagedInCurrentMonth(arr) {
            self.pastMonthUsersMessagedInCurrentMonth = arr;
        },
        setFormsData(arr) {
            self.formsData = arr;
        },
        setStatsData(arr) {
            self.statsData = arr;
        },
        setLoading(value) {
            self.loading = value;
        },
        setStatsTabKeyForUnmount(key) {
            self.statsTabKey = key;
        },
        setStatsTabKey(key) {
            self.reset();
            self.statsTabKey = key;
            if (key === 1) {
                self.getUsersCount();
                if (!IS_NIDA) {
                    const currentMonthName = getMonthName(0, 'MMM');
                    self.setSelectedMonthForIllinoisQT(currentMonthName);
                    self.setSelectedMonthForRhodeIslandQT(currentMonthName);
                    self.setSelectedMonthForMassachusettsQT(currentMonthName);
                    self.setSelectedMonthForDelawareQT(currentMonthName);
                    self.fetchUsersQTData({ isMultipleTerritoriesDataRequired: true });
                }
                return;
            } else if (key === 5) {
                self.setFilterBy(FILTER_BY_ALL_FORMS_FLAG);
                self.getFormsData();
                return;
            } else if (key === 6) {
                const allTerritoriesNames = Object.keys(toJS(SiteStore.allTerritories));
                self.fetchAppHealthData();
                self.getUsersWauData();
                self.fetchOverviewData();
                self.getRetentionData();
                self.fetchUsersWauDataBySites({
                    fetchType: USERS_WAU_DATA_FETCH_FLAGS_TERRITORY,
                    territory: [...allTerritoriesNames]
                });
                return;
            } else if (key === 2 || key === 3 || key === 4) {
                const { allSites } = SiteStore;
                // set initial territory to all territories
                self.setSelectedTerritoryForStatsUsers(TERRITORIES_TYPE_ALL_TERRITORIES);
                self.getUsersList();
                if (toJS(allSites) && !toJS(self.allTerritories).length) {
                    // fetch territories by sites if admin comes to case load section after changing the tab in stats page
                    self.fetchAllTerritoriesBySites(toJS(allSites));
                }
                return;
            } else {
                //  we will reset the pagination if admin comes to case load section
                const { allSites } = SiteStore;
                self.resetPagination();
                // set initial territory to all territories
                self.setSelectedTerritoryForCaseLoad(TERRITORIES_TYPE_ALL_TERRITORIES);
                if (toJS(allSites)) {
                    // fetch territories by sites if admin comes to case load section after changing the tab in stats page
                    self.fetchAllTerritoriesBySites(toJS(allSites));
                }
                return;
            }
        },
    }))
    .views((self) => ({
    }));

export default Stats;